/** Mirrors of the C# annual-procurement-plan and enrolment DTO records, field for field. */

// ── The enrolment: which units may file ────────────────────────────────────────────────

/** One enrolled department — an org unit that may file a PPMP. */
export interface PpmpDepartmentListItem {
  id: string;
  orgUnitId: string;
  /** Resolved through the join on every read — renaming an office corrects every roster. */
  orgUnitName: string;
  /** The unit's path code. Its place in the chart, not a business code. */
  orgUnitCode: string | null;
  orgUnitTypeName: string | null;
  /**
   * Whether the unit itself is still in the chart.
   *
   * A unit deleted AFTER being enrolled keeps its enrolment, so the LIST marks it rather than
   * dropping it — a department that vanished with no explanation is the worse failure, because
   * its PPMPs are still filed under it. The ROSTER excludes it; two surfaces, two questions.
   */
  isOrgUnitActive: boolean;
  /** How many PPMPs it has filed, across every plan. What the withdraw dialog counts. */
  ppmpCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** One org unit the enrolment form may add — only those not already enrolled. */
export interface PpmpDepartmentCandidate {
  orgUnitId: string;
  name: string;
  /** Ancestors joined with a chevron — a path through the chart, drawn as the option's sublabel. */
  path: string | null;
  orgUnitTypeName: string | null;
}

/**
 * Enrols one org unit.
 *
 * There is nothing to UPDATE on an enrolment — the row holds only the link — so this module has
 * no edit form and its row menu offers Withdraw and Restore and nothing between them.
 */
export interface PpmpDepartmentRequest {
  orgUnitId: string;
}

// ── The annual procurement plan ────────────────────────────────────────────────────────

/** One annual procurement plan, as the register shows it. */
export interface AnnualProcurementPlanListItem {
  id: string;
  appNumber: string;
  budgetPlanId: string;
  /** The budget plan's own number, live. What a reader recognises the frame by. */
  budgetPlanNumber: string;
  /** THE PLAN'S, joined on every read. There is no fiscal-year column on this record. */
  fiscalYear: number;
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
  ppmpCount: number;
  /** The sum of its PPMPs' procurement lines. Derived — there is no total column. */
  estimatedBudget: number;
  description: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** One plan, with what its roster needs. */
export interface AnnualProcurementPlanDetail extends AnnualProcurementPlanListItem {
  /**
   * Whether the submission deadline is still ahead, read in Asia/Manila.
   *
   * DERIVED server-side and SENT, so the form's disabled reason and the endpoint's refusal
   * cannot disagree about whether a department may still file.
   */
  isSubmissionOpen: boolean;
}

/**
 * Creates or corrects a plan.
 *
 * No field for the APP number — it is server-assigned. No field for the fiscal year either: it
 * is the budget plan's, read through the join. `budgetPlanId` is honoured on a CREATE and
 * ignored on an update, because a plan is never re-pointed at another year.
 */
export interface AnnualProcurementPlanRequest {
  budgetPlanId: string;
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
  description: string | null;
  notes: string | null;
}

/** One budget plan the create form may frame a procurement plan with. */
export interface AnnualProcurementPlanCandidate {
  budgetPlanId: string;
  planNumber: string;
  fiscalYear: number;
  /** The form SEEDS its own three dates from these — a procurement plan covers the budget year. */
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
}

/**
 * How far one department has got under one plan.
 *
 * THREE members where the budget roster's has four: the fourth there is `BudgetFinalization`,
 * which only a reviewer can know, and an annual procurement plan has no review stage.
 */
export const PPMP_PROGRESS = {
  notStarted: 0,
  onGoing: 1,
  forReview: 2,
} as const;

/** One line of a plan's department roster — every enrolled unit, left-joined to what it filed. */
export interface AppDepartmentRow {
  /** The ENROLMENT's id, not the unit's — this row is one line of a roster. */
  ppmpDepartmentId: string;
  orgUnitId: string;
  orgUnitName: string;
  orgUnitCode: string | null;
  /** The wire carries the value AND its label, so nothing here re-derives the words. */
  progress: number;
  progressLabel: string;
  /** Live PPMPs this department has filed under THIS plan. */
  ppmpCount: number;
  /** How many procurement LINES, across those PPMPs. Not the same fact as the count above. */
  lineItemCount: number;
  estimatedBudget: number;
}
